import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Icon, type IconName } from './icon';

interface ToastOptions {
  icon?: IconName;
  duration?: number;
}

interface ToastItem {
  id: number;
  msg: string;
  icon?: IconName;
}

type ToastFn = (msg: string, opts?: ToastOptions) => void;

const ToastContext = createContext<ToastFn | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const seq = useRef(0);

  const toast = useCallback<ToastFn>((msg, opts = {}) => {
    const id = ++seq.current;
    setToasts((t) => [...t, { id, msg, icon: opts.icon }]);
    window.setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), opts.duration ?? 2600);
  }, []);

  const value = useMemo(() => toast, [toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {toasts.length > 0 && (
        <div className="wb-toast-wrap" aria-live="polite">
          <div className="wb-col wb-gap-8" style={{ alignItems: 'center' }}>
            {toasts.map((t) => (
              <div className="wb-toast" key={t.id}>
                {t.icon && <Icon name={t.icon} size={18} />}
                {t.msg}
              </div>
            ))}
          </div>
        </div>
      )}
    </ToastContext.Provider>
  );
}

/** Returns a `toast(msg, opts?)` fn; no-op outside a ToastProvider. */
export function useToast(): ToastFn {
  return useContext(ToastContext) ?? (() => {});
}
